import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import { useQuery } from '@apollo/client';

import { Neighborhood } from 'apolloClient/types';
import Button from 'components/Button';
import CbbDropdown from 'components/CbbAdaptiveDropdown';
import arrowLeft from 'public/images/arrow-left.svg';
import LineBetweenSections from 'components/LineBetweenSections';
import {
  FAVORITES_BUILDINGS_IN_NEIGHBORHOOD,
  NEIGHBORHOOD_CONDOS,
} from 'apolloClient/queries/neighborhood';

import Condo from '../../components/Neighborhood/Condo';
import arrowDown from '../../public/images/arrow-down.svg';
import { createSearchUrlByType } from 'components/Search/utils/listingsFiltersUtils';
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { defaultImage } from 'src/constants/constants';
import { useAuth } from 'components/Auth/AuthProvider';
import { FavoriteBuildingsResponse } from 'apolloClient/types/FavoriteBuilding';
import { useFavoriteBuilding } from 'components/SavedHouses/SavedFavoritesBuildingsContext';

export const loadingStep = 9;

const sortsMethods: Record<string, string> = {
  'Price Highest First': 'minSalePrice:DESC',
  'Price Lowest First': 'minSalePrice:ASC',
  'Year Built': 'yearBuilt:ASC',
};

interface NeighborhoodBuildingItem {
  id: number;
  slug: string;
  name: string;
  isPreconstruction: boolean | null;
  primaryAddress: string;
  yearBuilt: number | null;
  mainImageUrl: string | null;
  mainImageFormats: Record<string, { url: string }> | null;
  minSalePrice: string | null;
  maxSalePrice: string | null;
  minSaleBeds: number | null;
  maxSaleBeds: number | null;
}

interface NeighborhoodBuildingsResponse {
  data: NeighborhoodBuildingItem[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    pageCount: number;
  };
}

const getImageUrl = (item: NeighborhoodBuildingItem): string => {
  const formats = item.mainImageFormats as Record<
    string,
    { url: string }
  > | null;
  return (
    formats?.small?.url || formats?.thumbnail?.url || item.mainImageUrl || ''
  );
};

const NeighborhoodCondos: React.FC<{
  neigborhood: Neighborhood;
  callbackBuildings: (data: boolean) => void;
}> = ({ neigborhood, callbackBuildings }) => {
  const { slug, name, id } = neigborhood;
  const [sortDropdownValue, setSortDropdownMethod] = useState(
    'Price Highest First'
  );

  useEffect(() => {
    setSortDropdownMethod('Price Highest First');
  }, [slug]);

  const { me } = useAuth();
  const { latestAction } = useFavoriteBuilding();

  const { data, loading, error, fetchMore } = useQuery<{
    getNeighborhoodBuildings: NeighborhoodBuildingsResponse;
  }>(NEIGHBORHOOD_CONDOS, {
    fetchPolicy: 'no-cache',
    variables: {
      sortBy: sortsMethods[sortDropdownValue],
      nhSlug: slug,
      page: 1,
      pageSize: loadingStep,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: favoritesData, refetch } = useQuery<{
    favoriteBuildings: FavoriteBuildingsResponse;
  }>(FAVORITES_BUILDINGS_IN_NEIGHBORHOOD, {
    fetchPolicy: 'no-cache',
    variables: {
      nhSlug: slug,
      userId: me?.id,
    },
  });

  const mappedFavoritesData = useMemo(
    () =>
      ({
        data: favoritesData?.favoriteBuildings.data.map((item) => ({
          attributes: {
            building: {
              ...item.attributes.building,
              id: (
                item.attributes.building as unknown as { data: { id: number } }
              ).data.id,
            },
          },
          id: item.id,
        })),
      } as FavoriteBuildingsResponse),
    [favoritesData]
  );

  useEffect(() => {
    if (
      data?.getNeighborhoodBuildings?.data?.some(
        (b) => latestAction?.buildingId === b.id
      )
    )
      refetch();
  }, [latestAction]);

  const buildings = data?.getNeighborhoodBuildings?.data ?? [];

  const everHadBuildings = useRef(false);
  if (buildings.length > 0) everHadBuildings.current = true;

  const addMoreBuildings = () => {
    const currentPage = data?.getNeighborhoodBuildings?.meta?.page || 1;
    const nextPage = currentPage + 1;
    fetchMore({
      variables: { page: nextPage },
      updateQuery(previousResult, { fetchMoreResult }) {
        if (!fetchMoreResult) return previousResult;
        return {
          getNeighborhoodBuildings: {
            ...fetchMoreResult.getNeighborhoodBuildings,
            data: [
              ...previousResult.getNeighborhoodBuildings.data,
              ...fetchMoreResult.getNeighborhoodBuildings.data,
            ],
          },
        };
      },
    });
  };

  function renderBuildings(items: NeighborhoodBuildingItem[]) {
    return items.map((building) => (
      <div key={building.id}>
        <Condo
          href={`/${slug}/${building.slug}`}
          imageUrl={getImageUrl(building)}
          blurredImageUrl={defaultImage}
          imageAlt={`${building.name} condos`}
          name={building.name || ''}
          buildingStatus={building.isPreconstruction || false}
          address={building.primaryAddress || ''}
          minSalePrice={Number(building.minSalePrice) || 0}
          maxSalePrice={Number(building.maxSalePrice) || 0}
          yearBuilt={building.yearBuilt || 0}
          minSaleBeds={building.minSaleBeds || 0}
          maxSaleBeds={building.maxSaleBeds || 0}
          buildingId={building.id}
          favoriteBuildings={mappedFavoritesData}
        />
      </div>
    ));
  }

  const Preloader = () => (
    <>
      {new Array(1).fill(null).map((_, i) => (
        <Box pt={3} key={i}>
          {new Array(10).fill(null).map((__, j) => (
            <Typography key={j} variant="h3">
              <Skeleton />
            </Typography>
          ))}
          <Divider sx={{ marginTop: 3 }} />
        </Box>
      ))}
    </>
  );

  useEffect(() => {
    if (buildings.length > 0) {
      callbackBuildings(true);
    }
  }, [buildings.length, callbackBuildings]);

  const meta = data?.getNeighborhoodBuildings?.meta;

  return (
    <>
      {everHadBuildings.current && (
        <>
          <section className="container pt-3 md:pt-[28px] lg:pt-[35px] text-m">
            <div className="flex flex-col justify-between md:items-center md:flex-row">
              <div className="flex items-center w-2/3">
                <div className="items-center justify-center hidden w-10 mx-2 md:flex lg:w-16">
                  <div className="w-10 lg:w-16 h-0.5 bg-black" />
                </div>
                <div className="justify-center text-23-29-0.77 uppercase lg:text-30-47-1 md:mx-4 font-sohneBreitBuch">
                  <button className="flex items-start text-left uppercase">
                    <div className="hidden w-8 mr-1">
                      <Image src={arrowDown} alt={`${name} Condos`} />
                    </div>
                    <h2>{name} Condos</h2>
                  </button>
                </div>
              </div>
              <CbbDropdown
                onChange={setSortDropdownMethod}
                rootClassName="h-12 w-60 md:w-[226px] mt-4 md:mt-0 text-14-18-0.3 md:pl-[16px] lg:pt-[7px] lg:pb-[9px] py-0 pb-[3px]"
                selectClassName="w-60 md:w-[226px] left-[-1px] top-[46px] mt-0"
                iconClasses="flex md:contents mt-[1px]"
                nameSelected={sortDropdownValue}
                textBeforeSelect="Sort by: "
                externalStore={true}
                options={[
                  'Price Highest First',
                  'Price Lowest First',
                  'Year Built',
                ]}
              />
            </div>
            {!everHadBuildings.current && loading ? (
              <div></div>
            ) : (
              <div className="grid grid-cols-1 gap-4 mt-6 mb-4 md:grid-cols-2 md:gap-[30px] lg:gap-[40px] xl:grid-cols-3">
                {renderBuildings(buildings)}
              </div>
            )}
            {meta &&
              buildings.length >= loadingStep &&
              meta.total &&
              buildings.length < meta.total && (
                <div className="flex justify-center">
                  <button
                    onClick={() => addMoreBuildings()}
                    className="flex items-center mt-4 uppercase border-b-2 cursor-pointer border-gold-dark md:mt-8 md:mb-10 lg:my-6 w-max"
                  >
                    <div className="w-5 h-4">
                      <Image src={arrowLeft} alt="more" width={15} />
                    </div>
                    <span className="whitespace-nowrap">More</span>
                  </button>
                </div>
              )}
          </section>
          <div className="container pb-3 mt-10 md:mt-0 md:px-12 md:pt-5 md:pb-[70px] lg:py-2 lg:pb-[73px] text-m">
            <div className="flex flex-col items-center justify-center mx-auto mt-10 space-y-8 xl:flex-row xl:space-y-0 xl:space-x-[16px] lg:w-1/2">
              <Button
                fontClassName="font-sohneBreitHalbfett xl:pb-[1.5px] lg:pb-[1px]"
                styles={{ paddingTop: '15px' }}
                classNames="h-[48px]"
                name={`${name} Condos for sale`}
                href={createSearchUrlByType('sale', { neighborhoods: [slug] })}
                classNameWidth="lg:pl-[19px]"
              />
              <Button
                fontClassName="font-sohneBreitHalbfett xl:pb-[1.5px] lg:pb-[1px]"
                styles={{ paddingTop: '15px' }}
                classNames="h-[48px]"
                name={`${name} Condos for rent`}
                href={createSearchUrlByType('rent', { neighborhoods: [slug] })}
                classNameWidth="xl:pl-[19px]"
              />
            </div>
          </div>
          <LineBetweenSections hideLine={'container'} />
        </>
      )}
    </>
  );
};

export default NeighborhoodCondos;
